import type React from 'react';

import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useHeaderHeight } from '#src/app/app/layout';

import { NAV_ITEMS } from './constants';
import { useSideNav } from './hooks/useSideNav';

/**
 * Desktop-only scrollspy dot nav for the ambassadors landing page. Every nav item is a
 * real anchor to its section id (working without JS as a plain hash jump); clicking one
 * smooth-scrolls (reduced-motion aware) and the scroll-spy marks the section currently in
 * view with `aria-current`. Hidden below `lg` so it never overlaps content on small screens.
 */
export const SideNav: React.FC<EmptyProps> = () => {
	const { translate } = useI18n();
	const { desktop: headerHeight } = useHeaderHeight();
	const {
		data: { activeId },
		actions: { handleNavItemClick },
	} = useSideNav();

	return (
		// Sticky, not fixed, so the rail stops with the sections it belongs to instead of riding on
		// over the CTA band, where its labels are unreadable against the red. The wrapper is
		// absolutely positioned in the layout's section stack, so it spans exactly that range;
		// zero-width and pointer-events-none so it can't cover or intercept page content, with the
		// nav itself overflowing it.
		<div className="pointer-events-none absolute inset-y-0 left-4 hidden w-0 lg:block">
			<nav
				aria-label={translate('corgi.ambassadors.side_nav.aria_label')}
				className="pointer-events-auto sticky z-20 w-max"
				style={{ top: headerHeight + 32 }}
			>
				<ul className="flex flex-col gap-2">
					{NAV_ITEMS.map(({ id, label }) => {
						const isActive = activeId === id;

						return (
							<li key={id}>
								<a
									href={`#${id}`}
									aria-current={isActive ? 'location' : undefined}
									onClick={(event) => {
										// Keep the URL clean and animate via the scroll-spy instead of the
										// browser's instant hash jump; the `href` is the no-JS fallback.
										event.preventDefault();
										handleNavItemClick(id);
									}}
									// `text-xs` lives here rather than on the label span: `cn`/tailwind-merge can't tell a
									// custom colour name (`text-typography-weak`) from a font size (`text-xs`), so putting
									// both on one element silently drops the colour and the label inherits
									// typography-strong. Keeping them on separate elements avoids the collision.
									// gap only while the label is open — with a collapsed label the gap is the anchor's
									// entire trailing width, and it would keep a few px of the nav hit-testable over the
									// page content beside it. `hover:`/`focus-visible:` rather than `group-*` because
									// this element is itself the group.
									className="group flex items-center gap-0 py-1.5 text-xs no-underline hover:gap-3 focus-visible:gap-3 min-[1600px]:gap-3"
								>
									<span
										aria-hidden="true"
										className={cn(
											'h-2 w-2 shrink-0 rounded-full transition-colors',
											isActive ? 'bg-fill-brand-strong' : 'bg-icon-weak group-hover:bg-icon-strong',
										)}
									/>
									<span
										className={cn(
											'font-semibold tracking-wide whitespace-nowrap uppercase',
											// An opaque pill, because below 1600px the label is revealed on hover and floats
											// over page content — `bg-fill-strong` has no alpha, so nothing bleeds through.
											'bg-fill-strong text-typography-inverse-strong rounded-full',
											// Width AND padding collapse when closed, not just opacity: an invisible label
											// still in layout leaves the fixed nav swallowing clicks meant for the page.
											'w-0 overflow-hidden px-0 py-1 opacity-0 transition-opacity',
											'group-hover:w-auto group-hover:px-2.5 group-hover:opacity-100',
											'group-focus-visible:w-auto group-focus-visible:px-2.5 group-focus-visible:opacity-100',
											// At 1600px labels sit permanently open in the clear margin, so they revert to
											// plain text. `!important` because `group-hover:` compiles to a compound selector
											// that outranks these on specificity — without it, hovering re-applies the pill.
											'min-[1600px]:w-auto min-[1600px]:opacity-100',
											'min-[1600px]:bg-fill-transparent! min-[1600px]:rounded-none! min-[1600px]:px-0!',
											isActive ? 'min-[1600px]:text-stroke-brand-strong!' : 'min-[1600px]:text-typography-weak!',
										)}
									>
										{label}
									</span>
								</a>
							</li>
						);
					})}
				</ul>
			</nav>
		</div>
	);
};
